import * as React from 'react';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import {
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import AppRoutes from './AppRoutes';
import { Copyright } from './Copyright';
import { useAccount, useMsal } from '@azure/msal-react';
import {
  AdminPanelSettings,
  Business,
  Logout,
  Person,
  Search,
  Star,
  StarBorder,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemIcon from '@mui/material/ListItemIcon';
import tenantService from '../pages/tenants/tenantService';
import useCheckPermission from '../hooks/useCheckPermission';
import { CollapseMenu } from '../components/CollapseMenu';
import { NavLink, useNavigate } from 'react-router';
import { resolveCmsBranding } from '../branding/resolveCmsBranding';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import authService from '../services/authService';
import accountService from '../services/accountService';
import {
  AdminMode,
  readStoredAdminMode,
  userHasPlatformSaaSAccess,
  writeStoredAdminMode,
} from '../utils/adminMode';
import type { MenuAdminScope } from './listItems';
import { menuItems } from './listItems';

const matchesAdminScope = (scope: MenuAdminScope | undefined, mode: AdminMode) => {
  const resolved = scope ?? 'tenant';
  if (resolved === 'both') return true;
  return resolved === mode;
};

const item = {
  py: 0.75,
  px: 2,
  mx: 1,
  borderRadius: 1.5,
};

const collapsedDrawerWidth = 72;
const drawerWidth: number = 280;

const MyNavLink = React.forwardRef<any, any>((props, ref) => (
  <NavLink
    ref={ref}
    to={props.to}
    className={({ isActive }) =>
      `${props.className} ${isActive ? props.activeClassName : ''}`
    }
  >
    {props.children}
  </NavLink>
));

const ActiveLink = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.info.light,
  '& .MuiSvgIcon-root': {
    color: '#FFFFFF',
    stroke: '#FFFFFF',
    fill: '#19ABC0',
  },
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: open ? drawerWidth : collapsedDrawerWidth,
  width: open
    ? `calc(100% - ${drawerWidth}px)`
    : `calc(100% - ${collapsedDrawerWidth}px)`,
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
    width: '100%',
  },
  ...(open && {
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    width: open ? drawerWidth : 0,
  },
  '& .MuiDrawer-paper': {
    position: 'fixed',
    whiteSpace: 'nowrap',
    width: open ? drawerWidth : collapsedDrawerWidth,
    overflowX: 'hidden',
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      width: open ? drawerWidth : 0,
    },
  },
}));

const DashboardContent = (): JSX.Element => {
  const [myTenants, setMyTenants] = React.useState<any[]>([]);
  const [activeTenant, setActiveTenant] = React.useState<any>('');
  const [accountMenuAnchor, setAccountMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [tenantActionPending, setTenantActionPending] = React.useState(false);
  const [tenantFilter, setTenantFilter] = React.useState('');
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();

  const {
    canPlatform,
    canTenant,
  } = useCheckPermission();
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const [open, setOpen] = React.useState(false);
  const featureFlags = useSelector((state: any) => state.app.featureFlags);
  const tenantBranding = useSelector((state: any) => state.app.tenantBranding);
  const adminMode = useSelector((state: any) => state.app.adminMode) as AdminMode;
  const platformPermissions = useSelector(
    (state: any) => state.user.userInfo?.platformPermissions
  );
  const userInfoFromStore = useSelector((state: any) => state.user.userInfo);
  const hasTenantMembershipFlag = useSelector(
    (state: any) => !!state.user.userInfo?.hasTenantMembership
  );
  const activeTenantId = useSelector(
    (state: any) => state.user.userInfo?.activeTenantId
  );
  const cmsBranding = React.useMemo(
    () => resolveCmsBranding(tenantBranding),
    [tenantBranding]
  );
  const mdTheme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: cmsBranding.primaryColor,
          },
          secondary: {
            main: cmsBranding.secondaryColor,
          },
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                '*::-webkit-scrollbar': {
                  width: '0px',
                },
              },
            },
          },
        },
      }),
    [cmsBranding.primaryColor, cmsBranding.secondaryColor]
  );
  const multiTenancyEnabled = !!featureFlags?.multiTenancyEnabled;
  const isSaasAdmin = userHasPlatformSaaSAccess(platformPermissions);
  const hasTenantAccess =
    !multiTenancyEnabled || myTenants.length > 0 || hasTenantMembershipFlag;
  // Platform-only SaaS admin stays in saas mode (no tenant CMS without membership).
  const effectiveAdminMode: AdminMode = !multiTenancyEnabled
    ? 'tenant'
    : isSaasAdmin
      ? hasTenantAccess
        ? adminMode
        : 'saas'
      : 'tenant';

  const menuAllows = React.useCallback(
    (scope: MenuAdminScope | undefined, requiredPermissions: string[]) => {
      const resolved = scope ?? 'tenant';
      if (resolved === 'saas') {
        return canPlatform(requiredPermissions);
      }
      if (resolved === 'both') {
        return (
          canPlatform(requiredPermissions) || canTenant(requiredPermissions)
        );
      }
      return canTenant(requiredPermissions);
    },
    [canPlatform, canTenant]
  );

  const visibleMenuItems = React.useMemo(
    () =>
      menuItems
        .filter(
          (item) => item.id !== 'Parkolás' || featureFlags?.parkingEnabled
        )
        .map((item) => ({
          ...item,
          children: item.children.filter((child) => {
            const scope = child.adminScope ?? item.adminScope ?? 'tenant';
            if (!matchesAdminScope(scope, effectiveAdminMode)) {
              return false;
            }
            if (child.id === 'HTML appletek' && !featureFlags?.htmlAppletsEnabled) {
              return false;
            }
            if (child.id === 'Applikáció témák' && !featureFlags?.appThemeEnabled) {
              return false;
            }
            if (
              !multiTenancyEnabled &&
              ['Bérlők', 'Bérlőcsoportok', 'Bérlő szerepkörök', 'Előfizetések', 'Licencek'].includes(
                child.id
              )
            ) {
              return false;
            }
            return true;
          }),
        }))
        .filter((item) => item.children.length > 0),
    [
      featureFlags?.parkingEnabled,
      featureFlags?.htmlAppletsEnabled,
      featureFlags?.appThemeEnabled,
      multiTenancyEnabled,
      effectiveAdminMode,
    ]
  );

  const filteredTenants = React.useMemo(() => {
    const query = tenantFilter.trim().toLocaleLowerCase('hu');
    const list = !query
      ? [...myTenants]
      : myTenants.filter((t) =>
          String(t.tenantName ?? '')
            .toLocaleLowerCase('hu')
            .includes(query)
        );
    return list.sort((a, b) => {
      if (a.tenantId === activeTenant) return -1;
      if (b.tenantId === activeTenant) return 1;
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return String(a.tenantName ?? '').localeCompare(
        String(b.tenantName ?? ''),
        'hu',
        { sensitivity: 'base' }
      );
    });
  }, [myTenants, tenantFilter, activeTenant]);

  const showTenantSearch = myTenants.length > 5;

  React.useEffect(() => {
    if (!multiTenancyEnabled) {
      if (adminMode !== 'tenant') {
        dispatch({ type: 'SET_ADMIN_MODE', payload: 'tenant' });
      }
      return;
    }
    if (!isSaasAdmin) {
      if (adminMode !== 'tenant') {
        dispatch({ type: 'SET_ADMIN_MODE', payload: 'tenant' });
      }
      return;
    }
    if (!hasTenantAccess) {
      if (adminMode !== 'saas') {
        writeStoredAdminMode('saas');
        dispatch({ type: 'SET_ADMIN_MODE', payload: 'saas' });
      }
      return;
    }
    if (readStoredAdminMode() === null) {
      writeStoredAdminMode('saas');
      dispatch({ type: 'SET_ADMIN_MODE', payload: 'saas' });
    }
  }, [isSaasAdmin, multiTenancyEnabled, hasTenantAccess, adminMode, dispatch]);

  const setAdminMode = (mode: AdminMode) => {
    writeStoredAdminMode(mode);
    dispatch({ type: 'SET_ADMIN_MODE', payload: mode });
    setAccountMenuAnchor(null);
    setTenantFilter('');
    if (mode === 'saas') {
      setActiveTenant('');
      dispatch({
        type: 'LOAD_USER_INFO',
        payload: {
          ...(userInfoFromStore ?? {}),
          activeTenantId: null,
          ActiveTenantId: null,
          isPlatformScope: true,
          tenantPermissions: [],
          TenantPermissions: [],
        },
      });
      dispatch({ type: 'LOAD_TENANT_BRANDING', payload: null });
      navigate('/platform');
      // Refresh from API with X-Admin-Scope: platform
      accountService.getUserInfo().then((userResponse) => {
        if (userResponse) {
          dispatch({ type: 'LOAD_USER_INFO', payload: userResponse });
        }
      });
    } else {
      navigate('/');
    }
  };
  const toggleDrawer = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    if (!activeTenantId) {
      dispatch({ type: 'LOAD_TENANT_BRANDING', payload: null });
      return;
    }

    const loadedForTenant = String(
      tenantBranding?.tenantId ?? (tenantBranding as any)?.TenantId ?? ''
    ).toLowerCase();
    if (
      loadedForTenant &&
      loadedForTenant === String(activeTenantId).toLowerCase()
    ) {
      return;
    }

    tenantService.getBranding(activeTenantId).then((response) => {
      if (!response.hasError) {
        dispatch({ type: 'LOAD_TENANT_BRANDING', payload: response.result });
      }
    });
  }, [activeTenantId, dispatch, tenantBranding?.tenantId]);

  React.useEffect(() => {
    document.title = `${cmsBranding.productName} CMS`;
  }, [cmsBranding.productName]);

  React.useEffect(() => {
    if (!multiTenancyEnabled) {
      setMyTenants([]);
      setActiveTenant('');
      return;
    }
    tenantService.listMyTenants().then((tenants) => {
      setMyTenants(tenants);
      const sessionId = String(activeTenantId ?? '').toLowerCase();
      const preferred =
        tenants.find(
          (t: any) => String(t.tenantId ?? '').toLowerCase() === sessionId
        )?.tenantId ??
        tenants.find((t: any) => t.isDefault)?.tenantId ??
        tenants[0]?.tenantId ??
        '';
      setActiveTenant(preferred);
    });
  }, [multiTenancyEnabled, activeTenantId]);

  const sameTenantId = React.useCallback(
    (a: any, b: any) =>
      !!a &&
      !!b &&
      String(a).toLowerCase() === String(b).toLowerCase(),
    []
  );

  const enterTenantCms = React.useCallback(
    async (tenantId: string) => {
      if (tenantActionPending || !tenantId) {
        return;
      }

      setTenantActionPending(true);
      setAccountMenuAnchor(null);
      setTenantFilter('');
      writeStoredAdminMode('tenant');
      dispatch({ type: 'SET_ADMIN_MODE', payload: 'tenant' });
      // Leave /platform immediately so PlatformDashboard cannot remount and
      // reset adminMode back to saas while changeTenant is in flight.
      navigate('/', { replace: true });
      dispatch({
        type: 'SHOW',
        payload: 'Bérlőváltás…',
      });

      const finishOk = async (resolvedId: string) => {
        setActiveTenant(resolvedId);
        try {
          const userResponse = await accountService.getUserInfo();
          if (userResponse) {
            dispatch({
              type: 'LOAD_USER_INFO',
              payload: {
                ...userResponse,
                activeTenantId:
                  userResponse.activeTenantId ??
                  userResponse.ActiveTenantId ??
                  resolvedId,
              },
            });
          } else {
            dispatch({
              type: 'LOAD_USER_INFO',
              payload: {
                ...(userInfoFromStore ?? {}),
                activeTenantId: resolvedId,
                ActiveTenantId: resolvedId,
                isPlatformScope: false,
              },
            });
          }

          dispatch({ type: 'LOAD_TENANT_BRANDING', payload: null });
          const branding = await tenantService.getBranding(resolvedId);
          if (!branding.hasError) {
            dispatch({ type: 'LOAD_TENANT_BRANDING', payload: branding.result });
          }
        } finally {
          dispatch({ type: 'HIDE' });
          setTenantActionPending(false);
        }
      };

      const failBackToSaas = (message?: string) => {
        writeStoredAdminMode('saas');
        dispatch({ type: 'SET_ADMIN_MODE', payload: 'saas' });
        dispatch({ type: 'HIDE' });
        setTenantActionPending(false);
        enqueueSnackbar(
          message || 'Bérlőváltás sikertelen. Platform nézetben maradtál.',
          { variant: 'error' }
        );
        navigate('/platform', { replace: true });
      };

      try {
        if (sameTenantId(tenantId, activeTenantId)) {
          await finishOk(tenantId);
          return;
        }

        const response = await tenantService.changeTenant({ tenantId });
        if (response.hasError) {
          failBackToSaas(
            (response.errorMessages ?? []).filter(Boolean).join(', ') ||
              undefined
          );
          return;
        }

        const resolved = String(
          response.result?.tenantId ??
            response.result?.TenantId ??
            tenantId
        );
        await finishOk(resolved);
      } catch (error: any) {
        const apiMessage =
          error?.response?.data?.message ||
          error?.response?.data?.errorMessages?.join?.(',') ||
          undefined;
        failBackToSaas(apiMessage);
      }
    },
    [
      activeTenantId,
      dispatch,
      enqueueSnackbar,
      navigate,
      sameTenantId,
      tenantActionPending,
      userInfoFromStore,
    ]
  );

  // set open to false if isSmallScreen is true
  React.useEffect(() => {
    setOpen(!isSmallScreen);
  }, [isSmallScreen]);

  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar position="absolute" open={open}>
          <Toolbar
            sx={{
              px: { xs: 1.5, sm: 2 },
              gap: 1.5,
              minHeight: { xs: 56, sm: 64 },
            }}
          >
            {isSmallScreen && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Menü"
                onClick={toggleDrawer}
              >
                <MenuIcon />
              </IconButton>
            )}
            {(!open || isSmallScreen) && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  display: { xs: 'flex', sm: open ? 'none' : 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                  p: 0.5,
                }}
              >
                <img
                  alt={cmsBranding.logoAlt}
                  src={cmsBranding.logoSrc}
                  style={{
                    maxHeight: '100%',
                    maxWidth: '100%',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
            <Typography
              component="h1"
              variant="h6"
              noWrap
              sx={{
                color: 'inherit',
                flexGrow: 1,
                minWidth: 0,
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                display: {
                  xs: 'block',
                  sm: open ? 'none' : 'block',
                },
              }}
            >
              {cmsBranding.appBarTitle}
            </Typography>
            <Box sx={{ flexGrow: open && !isSmallScreen ? 1 : 0 }} />
            <Tooltip title="Fiók">
              <IconButton
                color="inherit"
                onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                aria-label="Fiók menü"
                aria-controls={
                  accountMenuAnchor ? 'account-menu' : undefined
                }
                aria-haspopup="true"
                aria-expanded={accountMenuAnchor ? 'true' : undefined}
              >
                <Person />
              </IconButton>
            </Tooltip>
            <Menu
              id="account-menu"
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={() => {
                setAccountMenuAnchor(null);
                setTenantFilter('');
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: { sx: { width: 320, mt: 0.5 } },
              }}
            >
              <Box sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
                  {account?.name ?? 'Fiók'}
                </Typography>
                {account?.username && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block', mt: 0.25 }}
                  >
                    {account.username}
                  </Typography>
                )}
              </Box>
              <Divider />
              {multiTenancyEnabled &&
                isSaasAdmin &&
                effectiveAdminMode === 'tenant' && (
                  <>
                    <MenuItem
                      onClick={() => setAdminMode('saas')}
                    >
                      <ListItemIcon>
                        <AdminPanelSettings fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Platform adminisztráció"
                        secondary="SaaS bérlők, előfizetések, licencek"
                        slotProps={{
                          secondary: { sx: { fontSize: '0.7rem' } },
                        }}
                      />
                    </MenuItem>
                    <Divider />
                  </>
                )}
              {multiTenancyEnabled &&
                myTenants.length > 0 &&
                (effectiveAdminMode === 'tenant' ||
                  (effectiveAdminMode === 'saas' && hasTenantAccess)) && (
                <>
                  <Box sx={{ px: 2, pt: 1.25, pb: showTenantSearch ? 1 : 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {myTenants.length > 1
                        ? `Bérlők (${myTenants.length})`
                        : 'Bérlő'}
                    </Typography>
                    {showTenantSearch && (
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Keresés bérlő neve alapján…"
                        value={tenantFilter}
                        onChange={(e) => setTenantFilter(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{ mt: 1 }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search fontSize="small" color="action" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      maxHeight: showTenantSearch ? 280 : 220,
                      overflowY: 'auto',
                    }}
                  >
                    {filteredTenants.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ px: 2, py: 1.5 }}
                      >
                        Nincs találat.
                      </Typography>
                    ) : (
                      filteredTenants.map((tenant) => {
                        const sessionTenantId = activeTenantId || activeTenant;
                        const isSessionTenant = sameTenantId(
                          tenant.tenantId,
                          sessionTenantId
                        );
                        // CMS mode only — platform módban nincs kiválasztott menüpont.
                        const isActive =
                          effectiveAdminMode === 'tenant' && isSessionTenant;
                        const isDefault = !!tenant.isDefault;
                        return (
                          <MenuItem
                            key={tenant.tenantId}
                            selected={isActive}
                            disabled={tenantActionPending}
                            onClick={() => {
                              if (tenantActionPending) {
                                return;
                              }
                              if (effectiveAdminMode === 'saas') {
                                enterTenantCms(String(tenant.tenantId));
                                return;
                              }
                              if (isActive || myTenants.length < 2) {
                                return;
                              }
                              enterTenantCms(String(tenant.tenantId));
                            }}
                            sx={{
                              alignItems: 'flex-start',
                              py: 1,
                              gap: 0.5,
                              ...(isActive
                                ? {
                                    bgcolor: 'action.selected',
                                    borderLeft: 3,
                                    borderColor: 'primary.main',
                                    pl: 1.25,
                                    '&:hover': {
                                      bgcolor: 'action.selected',
                                    },
                                  }
                                : {}),
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                              <Business
                                fontSize="small"
                                color={isActive ? 'primary' : 'inherit'}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  noWrap
                                  sx={{
                                    fontWeight: isActive ? 700 : 500,
                                    fontSize: '0.875rem',
                                    color: isActive
                                      ? 'primary.main'
                                      : 'text.primary',
                                  }}
                                >
                                  {tenant.tenantName}
                                </Typography>
                              }
                              secondary={
                                isDefault
                                  ? 'Alapértelmezett belépéskor'
                                  : effectiveAdminMode === 'saas'
                                    ? 'Kattints a CMS megnyitásához'
                                    : undefined
                              }
                              slotProps={{
                                secondary: { sx: { fontSize: '0.7rem' } },
                              }}
                            />
                            {myTenants.length > 1 && (
                              <Tooltip
                                title={
                                  isDefault
                                    ? 'Ez az alapértelmezett bérlő'
                                    : 'Beállítás alapértelmezettként'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      isDefault || tenantActionPending
                                    }
                                    aria-label={
                                      isDefault
                                        ? 'Alapértelmezett bérlő'
                                        : 'Beállítás alapértelmezettként'
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        isDefault ||
                                        tenantActionPending
                                      ) {
                                        return;
                                      }
                                      setTenantActionPending(true);
                                      tenantService
                                        .setDefaultTenant({
                                          tenantId: tenant.tenantId,
                                        })
                                        .then((response) => {
                                          if (response.hasError) {
                                            return;
                                          }
                                          setMyTenants((prev) =>
                                            prev.map((t) => ({
                                              ...t,
                                              isDefault:
                                                t.tenantId ===
                                                tenant.tenantId,
                                            }))
                                          );
                                        })
                                        .finally(() =>
                                          setTenantActionPending(false)
                                        );
                                    }}
                                  >
                                    {isDefault ? (
                                      <Star
                                        fontSize="small"
                                        color="warning"
                                      />
                                    ) : (
                                      <StarBorder fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </MenuItem>
                        );
                      })
                    )}
                  </Box>
                  <Divider />
                </>
              )}
              <MenuItem
                onClick={() => {
                  setAccountMenuAnchor(null);
                  setTenantFilter('');
                  navigate('/profile');
                }}
              >
                <ListItemIcon>
                  <Person fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Profil" />
              </MenuItem>
              <MenuItem
                onClick={async () => {
                  setAccountMenuAnchor(null);
                  setTenantFilter('');
                  if (authService.isLocalAuth()) {
                    await authService.revokeRefreshToken();
                    authService.clearSession();
                    window.location.hash = '#/';
                    window.location.reload();
                    return;
                  }
                  authService.clearSession();
                  await instance.logoutRedirect();
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Kijelentkezés" />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Drawer
          variant="permanent"
          open={open}
          sx={{ overflow: 'auto', maxHeight: '100vh' }}
        >
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: open ? 'space-between' : 'center',
              gap: 1,
              px: open ? 1.5 : 1,
              minHeight: { xs: 56, sm: 64 },
            }}
          >
            {open && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.25}
                sx={{ minWidth: 0, flex: 1, cursor: 'pointer' }}
                onClick={() => navigate('/')}
              >
                <Box
                  component="img"
                  alt={cmsBranding.logoAlt}
                  src={cmsBranding.logoSrc}
                  sx={{ height: 36, width: 36, objectFit: 'contain', flexShrink: 0 }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  sx={{ fontWeight: 800, letterSpacing: -0.3, fontSize: '1.15rem' }}
                >
                  CMS
                </Typography>
              </Stack>
            )}
            <IconButton
              onClick={toggleDrawer}
              size="small"
              aria-label={open ? 'Menü becsukása' : 'Menü kinyitása'}
            >
              {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
          </Toolbar>
          <Divider />
          <List
            component="nav"
            dense
            sx={{
              px: open ? 0.5 : 0.75,
              py: 1,
              '& .MuiListItemIcon-root': {
                color: 'primary.main',
                minWidth: open ? 40 : 0,
                justifyContent: 'center',
              },
              '& .MuiSvgIcon-root': {
                fontSize: 22,
              },
            }}
          >
            {open &&
              isSaasAdmin &&
              multiTenancyEnabled &&
              !hasTenantAccess &&
              effectiveAdminMode === 'saas' && (
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Platform mód — bérlői CMS csak meghívás után.
                  </Typography>
                  <Divider sx={{ mt: 1, mb: 0 }} />
                </Box>
              )}
            {visibleMenuItems.map(
              ({ id, children, icon, requiredPermissions, collapsed, adminScope }) =>
                menuAllows(adminScope ?? 'tenant', requiredPermissions) ? (
                  <CollapseMenu
                    key={id}
                    collapsed={collapsed}
                    icon={icon}
                    id={id}
                    minimal={!open}
                  >
                    {children.map(
                      ({
                        id: childId,
                        icon: childIcon,
                        to,
                        useHref,
                        requiredPermissions: childPerms,
                        adminScope: childScope,
                      }) =>
                        menuAllows(
                          childScope ?? adminScope ?? 'tenant',
                          childPerms
                        ) ? (
                          <Tooltip
                            key={childId}
                            title={childId}
                            placement="right"
                            disableHoverListener={open}
                          >
                            <Box component="span" sx={{ display: 'block' }}>
                              <ListItemButton
                                sx={{
                                  ...item,
                                  justifyContent: open
                                    ? 'flex-start'
                                    : 'center',
                                  px: open ? 2 : 1,
                                  py: open ? 0.85 : 1.1,
                                  minHeight: open ? 40 : 44,
                                }}
                                component={useHref ? 'a' : MyNavLink}
                                {...(useHref
                                  ? { href: to }
                                  : {
                                      activeClassName: ActiveLink,
                                      to,
                                    })}
                                onClick={() => {
                                  if (isSmallScreen) setOpen(false);
                                }}
                              >
                                <ListItemIcon>{childIcon}</ListItemIcon>
                                {open && <ListItemText primary={childId} />}
                              </ListItemButton>
                            </Box>
                          </Tooltip>
                        ) : null
                    )}
                  </CollapseMenu>
                ) : null
            )}
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light'
                ? theme.palette.grey[100]
                : theme.palette.grey[900],
            flexGrow: 1,
            overflow: 'auto',
            minHeight: '100vh',
            width: '100%',
            transition: mdTheme.transitions.create(['width'], {
              easing: mdTheme.transitions.easing.sharp,
              duration: mdTheme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Toolbar />
          <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box>
              <AppRoutes
                key={`routes-${effectiveAdminMode}-${activeTenantId || activeTenant || 'none'}`}
              />
            </Box>
            <Copyright sx={{ pt: 4 }} />
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default function Layout() {
  return <DashboardContent />;
}
